import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Tag, Space, Statistic, Row, Col, Alert, Modal, message, Select, InputNumber, Tooltip, Typography } from 'antd';
import { SafetyCertificateOutlined, CheckCircleOutlined, CloseCircleOutlined, ReloadOutlined, ExperimentOutlined, SettingOutlined } from '@ant-design/icons';
import { adminAPI, internalGatewayAPI } from '../services/api';

const { Title, Text } = Typography;

const ProviderHealthMonitor = () => {
  const [healthStatuses, setHealthStatuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [providers, setProviders] = useState([]);
  const [markModalVisible, setMarkModalVisible] = useState(false);
  const [markingProvider, setMarkingProvider] = useState(null);
  const [markDuration, setMarkDuration] = useState(30);
  const [routerMetrics, setRouterMetrics] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(false);

  useEffect(() => {
    fetchHealthStatuses();
    fetchProviders();
    fetchRouterMetrics();
    
    // 定时刷新健康状态（每 30 秒）
    const interval = setInterval(() => {
      fetchHealthStatuses();
      fetchRouterMetrics();
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const fetchHealthStatuses = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getHealthStatuses();
      if (response) {
        const data = response.data || response;
        // 确保 data 是数组
        setHealthStatuses(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('获取健康状态失败:', error);
      message.error('获取健康状态失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchProviders = async () => {
    try {
      const response = await adminAPI.getProviders();
      if (response.code === 0) {
        setProviders(response.data?.list || []);
      }
    } catch (error) {
      console.error('获取供应商列表失败:', error);
    }
  };

  const fetchRouterMetrics = async () => {
    setMetricsLoading(true);
    try {
      const response = await adminAPI.getRouterMetrics();
      if (response) {
        setRouterMetrics(response.data || response);
      }
    } catch (error) {
      console.error('获取路由指标失败:', error);
    } finally {
      setMetricsLoading(false);
    }
  };

  const handleMarkUnhealthy = async () => {
    if (!markingProvider) return;
    try {
      const response = await adminAPI.markProviderUnhealthy(markingProvider.id, markDuration);
      if (response.code === 0) {
        message.success(`已将 ${markingProvider.display_name || markingProvider.name} 标记为不健康 (${markDuration}分钟)`);
        setMarkModalVisible(false);
        fetchHealthStatuses();
      } else {
        message.error(response.message || '操作失败');
      }
    } catch (error) {
      message.error('操作失败');
    }
  };

  const handleMarkHealthy = async (provider) => {
    try {
      const response = await adminAPI.markProviderHealthy(provider.id);
      if (response.code === 0) {
        message.success(`已将 ${provider.display_name || provider.name} 标记为健康`);
        fetchHealthStatuses();
      } else {
        message.error(response.message || '操作失败');
      }
    } catch (error) {
      message.error('操作失败');
    }
  };

  const handleManualCheck = async (provider) => {
    try {
      const response = await internalGatewayAPI.post(`/health/providers/${provider.id}/check`);
      if (response.code === 0) {
        message.success(`已对 ${provider.display_name || provider.name} 进行健康检查`);
        fetchHealthStatuses();
      } else {
        message.error(response.message || '检查失败');
      }
    } catch (error) {
      message.error('健康检查失败');
    }
  };

  const getHealthColor = (isHealthy) => {
    return isHealthy ? '#52c41a' : '#ff4d4f';
  };

  const getHealthIcon = (isHealthy) => {
    return isHealthy ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
  };

  const getHealthTag = (isHealthy) => {
    return (
      <Tag color={isHealthy ? 'success' : 'error'} icon={getHealthIcon(isHealthy)}>
        {isHealthy ? '健康' : '不健康'}
      </Tag>
    );
  };

  const columns = [
    {
      title: '供应商',
      dataIndex: 'provider_name',
      key: 'provider_name',
      width: 150,
      render: (name, record) => {
        const provider = providers.find(p => p.id === record.provider_id);
        return (
          <Space direction="vertical" size={0}>
            <Text strong>{name || provider?.display_name || provider?.name || '-'}</Text>
            {provider && (
              <Text type="secondary" style={{ fontSize: 12 }}>{provider.name}</Text>
            )}
          </Space>
        );
      },
    },
    {
      title: '健康状态',
      dataIndex: 'is_healthy',
      key: 'is_healthy',
      width: 120,
      render: (isHealthy, record) => (
        <Space>
          {getHealthTag(isHealthy)}
          {record.fail_count > 0 && record.fail_count < 3 && (
            <Tooltip title={`连续失败 ${record.fail_count} 次`}>
              <Tag color="warning">{record.fail_count}/3</Tag>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: '最后检查',
      dataIndex: 'last_check',
      key: 'last_check',
      width: 180,
      render: (time) => {
        if (!time || time === '0001-01-01T00:00:00Z') return '-';
        const date = new Date(time);
        if (isNaN(date.getTime())) return '-';
        const now = new Date();
        const diff = Math.floor((now - date) / 1000);
        if (diff < 0) return '刚刚';
        if (diff < 60) return `${diff}秒前`;
        if (diff < 3600) return `${Math.floor(diff / 60)}分钟前`;
        if (diff < 86400) return `${Math.floor(diff / 3600)}小时前`;
        return date.toLocaleString();
      },
    },
    {
      title: '最后成功',
      dataIndex: 'last_success',
      key: 'last_success',
      width: 180,
      render: (time) => {
        if (!time || time === '0001-01-01T00:00:00Z') return <Text type="secondary">-</Text>;
        const date = new Date(time);
        if (isNaN(date.getTime())) return '-';
        const now = new Date();
        const diff = Math.floor((now - date) / 1000);
        if (diff < 0) return '刚刚';
        if (diff < 60) return `${diff}秒前`;
        if (diff < 3600) return `${Math.floor(diff / 60)}分钟前`;
        if (diff < 86400) return `${Math.floor(diff / 3600)}小时前`;
        return date.toLocaleString();
      },
    },
    {
      title: '最后失败',
      dataIndex: 'last_failure',
      key: 'last_failure',
      width: 180,
      render: (time) => {
        if (!time || time === '0001-01-01T00:00:00Z') return <Text type="secondary">无失败</Text>;
        const date = new Date(time);
        if (isNaN(date.getTime())) return '-';
        const now = new Date();
        const diff = Math.floor((now - date) / 1000);
        if (diff < 0) return '刚刚';
        if (diff < 60) return `${diff}秒前`;
        if (diff < 3600) return `${Math.floor(diff / 60)}分钟前`;
        if (diff < 86400) return `${Math.floor(diff / 3600)}小时前`;
        return date.toLocaleString();
      },
    },
    {
      title: '错误信息',
      dataIndex: 'error_msg',
      key: 'error_msg',
      ellipsis: true,
      render: (msg) => msg ? <Text type="danger">{msg}</Text> : <Text type="secondary">-</Text>,
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      fixed: 'right',
      render: (_, record) => {
        const provider = providers.find(p => p.id === record.provider_id);
        return (
          <Space size="small">
            <Tooltip title="手动检查">
              <Button 
                type="link" 
                size="small" 
                icon={<ExperimentOutlined />}
                onClick={() => handleManualCheck(provider || record)}
              >
                检查
              </Button>
            </Tooltip>
            {record.is_healthy ? (
              <Tooltip title="手动标记为不健康（用于维护）">
                <Button 
                  type="link" 
                  size="small" 
                  danger
                  onClick={() => {
                    setMarkingProvider(provider || record);
                    setMarkModalVisible(true);
                  }}
                >
                  禁用
                </Button>
              </Tooltip>
            ) : (
              <Tooltip title="手动标记为健康">
                <Button 
                  type="link" 
                  size="small" 
                  onClick={() => handleMarkHealthy(provider || record)}
                >
                  启用
                </Button>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
  ];

  // 计算统计数据
  const healthyCount = healthStatuses.filter(s => s.is_healthy).length;
  const unhealthyCount = healthStatuses.filter(s => !s.is_healthy).length;
  const totalCount = healthStatuses.length;

  return (
    <div style={{ padding: 24 }}>
      <Title level={4} style={{ marginBottom: 24 }}>
        <SafetyCertificateOutlined style={{ marginRight: 8 }} />
        供应商健康监控
      </Title>

      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic 
              title="供应商总数" 
              value={totalCount} 
              prefix={<SafetyCertificateOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic 
              title="健康" 
              value={healthyCount} 
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic 
              title="不健康" 
              value={unhealthyCount} 
              prefix={<CloseCircleOutlined />}
              valueStyle={{ color: unhealthyCount > 0 ? '#ff4d4f' : '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic 
              title="健康率" 
              value={(totalCount > 0 ? (healthyCount / totalCount * 100) : 100).toFixed(1)} 
              suffix="%"
              valueStyle={{ color: unhealthyCount > 0 ? '#faad14' : '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 路由指标 */}
      {routerMetrics && (
        <Card 
          title="路由指标" 
          style={{ marginBottom: 24 }}
          extra={<Button icon={<ReloadOutlined />} onClick={fetchRouterMetrics}>刷新</Button>}
          loading={metricsLoading}
        >
          <Row gutter={16}>
            <Col span={6}>
              <Statistic title="总请求数" value={routerMetrics.total_requests || 0} />
            </Col>
            <Col span={18}>
              <Text type="secondary">策略使用统计：</Text>
              <div style={{ marginTop: 8 }}>
                {routerMetrics.strategy_usage && Object.entries(routerMetrics.strategy_usage).map(([strategy, count]) => (
                  <Tag key={strategy} color="blue" style={{ marginRight: 8, marginBottom: 4 }}>
                    {strategy}: {count}
                  </Tag>
                ))}
              </div>
            </Col>
          </Row>
        </Card>
      )}

      {/* 健康状态表格 */}
      <Card
        title="供应商健康状态"
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={fetchHealthStatuses}>
              刷新状态
            </Button>
          </Space>
        }
      >
        {unhealthyCount > 0 && (
          <Alert
            message="有供应商不健康"
            description={`${unhealthyCount} 个供应商当前不可用，可能影响服务。请检查网络连接或 API 配置。`}
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        <Table
          columns={columns}
          dataSource={healthStatuses}
          rowKey={(record) => record.provider_id || record.provider_name}
          loading={loading}
          scroll={{ x: 1200 }}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      {/* 标记不健康弹窗 */}
      <Modal
        title="标记供应商为不健康"
        open={markModalVisible}
        onCancel={() => setMarkModalVisible(false)}
        onOk={handleMarkUnhealthy}
        okText="确认标记"
        okButtonProps={{ danger: true }}
      >
        <Alert
          message="维护模式"
          description="标记为不健康后，该供应商将被排除在路由之外，直到自动恢复或手动标记为健康。"
          type="info"
          style={{ marginBottom: 16 }}
        />
        <div style={{ marginTop: 16 }}>
          <Text strong>供应商：{markingProvider?.display_name || markingProvider?.name}</Text>
        </div>
        <div style={{ marginTop: 16 }}>
          <Text>不健康持续时间（分钟）：</Text>
          <InputNumber
            min={1}
            max={1440}
            value={markDuration}
            onChange={(val) => setMarkDuration(val || 30)}
            style={{ marginLeft: 8, width: 120 }}
          />
          <Text type="secondary" style={{ marginLeft: 8 }}>
            （0 = 永久，直到手动恢复）
          </Text>
        </div>
      </Modal>
    </div>
  );
};

export default ProviderHealthMonitor;
